import { ConnectResult, Data, PartialMessage, create, createPromiseClient } from '@/connect';
import {
  BrokerCancelOrdersRequestSchema,
  CancelAllOrdersResponse,
  GetActiveOrderCountResponse,
  OmsService,
  QueryOrdersFilter,
  QueryOrdersResponse,
  QueryOrdersSortSchema,
  SplitOrderRequest,
} from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import {
  AckExecutionsRequestSchema,
  AckExecutionsResponse,
  CancelExecutionRecordsRequestSchema,
  CancelExecutionRecordsResponse,
  CustomExportAndAckExecutionRecordsRequestSchema,
  CustomExportAndAckExecutionRecordsResponse,
  ExecutionService,
  ExecutionsReportType,
  QueryExecutionRecordsResponse,
} from '@/connect/gen/modules/apiengine/services/executions/executions_pb';
import { SortOrder, SortableField } from '@/connect/gen/consts/queryconsts_pb';
import { SortModelItem } from 'ag-grid-enterprise';

import {
  ExportMarketplaceRequest,
  ExportMarketplaceResponse,
  QueryMarketplaceInstrumentsRequest,
  QueryMarketplaceInstrumentsResponse,
  QueryMarketplaceRequest,
  QueryMarketplaceResponse,
  VenueService,
} from '@/connect/gen/modules/apiengine/services/venue/venue_pb';

export const marketplaceService = new (class {
  public readonly omsPromiseClient = createPromiseClient(OmsService);
  public readonly venuePromiseClient = createPromiseClient(VenueService);
  public readonly executionsPromiseClient = createPromiseClient(ExecutionService);

  public splitOrder(request: PartialMessage<SplitOrderRequest>) {
    return this.omsPromiseClient.splitOrder(request);
  }

  public queryOrders(config: {
    page: number;
    pageSize: number;
    sort: SortModelItem;
    filter: QueryOrdersFilter;
    signal: AbortSignal;
  }): Promise<ConnectResult<QueryOrdersResponse>> {
    const sortFields: Partial<Record<keyof Data<QueryOrdersFilter>, SortableField>> = {
      fee: SortableField.FEE,
      rebate: SortableField.REBATE,
      side: SortableField.SIDE,
      openQuantity: SortableField.OPEN_QUANTITY,
      totalQuantity: SortableField.TOTAL_QUANTITY,
      minQuantity: SortableField.MIN_QUANTITY,
      companyId: SortableField.COMPANY_NAME,
      cusip: SortableField.INSTRUMENT_CUSIP,
      isin: SortableField.INSTRUMENT_ISIN,
      ticker: SortableField.INSTRUMENT_TICKER,
      securityName: SortableField.SECURITY_NAME,
      securityTypes: SortableField.SECURITY_TYPE,
      securityCategories: SortableField.SECURITY_CATEGORY,
      timeInForce: SortableField.TIME_IN_FORCE,
      orderType: SortableField.TYPE,
      orderStatus: SortableField.STATUS,
      createdAt: SortableField.CREATED_AT,
      createdBy: SortableField.CREATED_BY,
      updatedAt: SortableField.UPDATED_AT,
      lastUpdatedBy: SortableField.LAST_UPDATED_BY,
      execQuantity: SortableField.FILLED_QUANTITY,
      avgExecutionFee: SortableField.AVG_EXEC_FEE,
      avgExecutionRebate: SortableField.AVG_EXEC_REBATE,
      goodTillTime: SortableField.GOOD_TILL_TIME,
      unitPrice: SortableField.UNIT_PRICE,
      priceChange: SortableField.PRICE_CHANGE,
      openMarketValue: SortableField.OPEN_MARKET_VALUE,
      percentagePar: SortableField.PERCENTAGE_PAR,
      orderRef: SortableField.ORDER_REF,
      boQuantity: SortableField.BO_QUANTITY,
      boFee: SortableField.BO_FEE,
      boRebate: SortableField.BO_REBATE,
      bbQuantity: SortableField.BB_QUANTITY,
      bbFee: SortableField.BB_FEE,
      bbRebate: SortableField.BB_REBATE,
      bboQuantity: SortableField.BBO_QUANTITY,
      bboFee: SortableField.BBO_FEE,
      bboRebate: SortableField.BBO_REBATE,
      bboSpreadPctRebate: SortableField.BBO_SPREAD_PCT_REBATE,
      bboSpreadVsFee: SortableField.BBO_SPREAD_VS_FEE,
      bboSpreadVsRebate: SortableField.BBO_SPREAD_VS_REBATE,
      bboAgreementIds: SortableField.BBO_AGREEMENT,
    };

    const field = config.sort.colId;
    let order = config.sort.sort === 'asc' ? SortOrder.ASC : SortOrder.DESC;
    if (field === 'rebate') {
      order = config.sort.sort === 'asc' ? SortOrder.DESC : SortOrder.ASC;
    }
    const sorts =
      field in sortFields
        ? [create(QueryOrdersSortSchema, { field: sortFields[field], order })]
        : [];
    const query = {
      filter: config.filter,
      sorts,
      pageNumber: config.page,
      pageLimit: config.pageSize,
    };

    return this.omsPromiseClient.queryOrders(query);
  }

  public querySecurities(
    config: PartialMessage<QueryMarketplaceInstrumentsRequest>,
    asCompanyId?: string // brokers can view-as-company
  ): Promise<ConnectResult<QueryMarketplaceInstrumentsResponse>> {
    return this.venuePromiseClient.queryMarketplaceInstruments(config, {
      ...(asCompanyId && { headers: { 'As-Company': asCompanyId } }),
    });
  }

  public queryMarketplace(
    config: PartialMessage<QueryMarketplaceRequest>,
    asCompanyId?: string // brokers can view-as-company
  ): Promise<ConnectResult<QueryMarketplaceResponse>> {
    return this.venuePromiseClient.queryMarketplace(config, {
      ...(asCompanyId && { headers: { 'As-Company': asCompanyId } }),
    });
  }

  public exportMarketplace(
    config: PartialMessage<ExportMarketplaceRequest>,
    asCompanyId?: string // brokers can export-as-company
  ): Promise<ConnectResult<ExportMarketplaceResponse>> {
    return this.venuePromiseClient.exportMarketplace(config, {
      ...(asCompanyId && { headers: { 'As-Company': asCompanyId } }),
    });
  }

  public async queryExecutions(
    config: {
      pageNumber: number;
      pageLimit: number;
      signal?: AbortSignal;
    },
    asCompanyId?: string // brokers can view-as-company
  ): Promise<ConnectResult<QueryExecutionRecordsResponse>> {
    const query = {
      // server-side filtering and sorting not yet implemented
      pageNumber: config.pageNumber,
      pageLimit: config.pageLimit,
    };
    return this.executionsPromiseClient.queryExecutionRecords(query, {
      ...(asCompanyId && { headers: { 'As-Company': asCompanyId } }),
    });
  }

  public acknowledgeExecutions(config: {
    executionIds: string[];
    signal?: AbortSignal;
  }): Promise<ConnectResult<AckExecutionsResponse>> {
    const request = create(AckExecutionsRequestSchema, {
      isAcknowledged: true,
      executionIds: config.executionIds,
    });
    return this.executionsPromiseClient.ackExecutions(request);
  }

  public cancelExecutionRecords(config: {
    executionIds: string[];
    signal?: AbortSignal;
  }): Promise<ConnectResult<CancelExecutionRecordsResponse>> {
    const request = create(CancelExecutionRecordsRequestSchema, {
      executionIds: config.executionIds,
    });
    return this.executionsPromiseClient.cancelExecutionRecords(request);
  }

  public customExportAndAck0101(config: {
    executionIds: string[];
    asCompanyId?: string; // brokers can view-as-company
  }): Promise<ConnectResult<CustomExportAndAckExecutionRecordsResponse>> {
    const request = create(CustomExportAndAckExecutionRecordsRequestSchema, {
      type: ExecutionsReportType.TYPE_0101,
      executionIds: config.executionIds.length ? config.executionIds : undefined,
    });
    return this.executionsPromiseClient.customExportAndAckExecutionRecords(request, {
      ...(config.asCompanyId && { headers: { 'As-Company': config.asCompanyId } }),
    });
  }

  public customExportAndAck7273(config: {
    executionIds: string[];
    asCompanyId?: string; // brokers can view-as-company
  }): Promise<ConnectResult<CustomExportAndAckExecutionRecordsResponse>> {
    const request = create(CustomExportAndAckExecutionRecordsRequestSchema, {
      type: ExecutionsReportType.TYPE_7273,
      executionIds: config.executionIds.length ? config.executionIds : undefined,
    });
    return this.executionsPromiseClient.customExportAndAckExecutionRecords(request, {
      ...(config.asCompanyId && { headers: { 'As-Company': config.asCompanyId } }),
    });
  }

  public customExportAndAckESEC(config: {
    executionIds: string[];
    asCompanyId?: string; // brokers can view-as-company
  }): Promise<ConnectResult<CustomExportAndAckExecutionRecordsResponse>> {
    const request = create(CustomExportAndAckExecutionRecordsRequestSchema, {
      type: ExecutionsReportType.TYPE_ESEC,
      executionIds: config.executionIds.length ? config.executionIds : undefined,
    });
    return this.executionsPromiseClient.customExportAndAckExecutionRecords(request, {
      ...(config.asCompanyId && { headers: { 'As-Company': config.asCompanyId } }),
    });
  }

  public cancelAllOrders(): Promise<ConnectResult<CancelAllOrdersResponse>> {
    return this.omsPromiseClient.cancelAllOrders({});
  }

  public getActiveOrderCount(): Promise<ConnectResult<GetActiveOrderCountResponse>> {
    return this.omsPromiseClient.getActiveOrderCount({});
  }

  public async brokerCancelOrders(
    orderRefs: string[],
    email: string
  ): Promise<{
    successfulOrderRefs: string[];
    failedOrderRefs: string[];
  }> {
    const request = create(BrokerCancelOrdersRequestSchema, {
      ids: orderRefs,
      email: email,
    });

    const result = await this.omsPromiseClient.brokerCancelOrders(request);

    if (!result.success) {
      return {
        successfulOrderRefs: [],
        failedOrderRefs: orderRefs,
      };
    }

    return {
      successfulOrderRefs: result.data.ids,
      failedOrderRefs: result.data.errors.map((error) => error.id),
    };
  }
})();
